import { useState } from 'react';
import { useCrews, useServiceRequests } from '../lib/queries';
import {
    useAdvanceServiceRequestStatus,
    useAssignServiceRequest,
    useCancelServiceRequest,
} from '../lib/mutations';
import { Button, EmptyState, Input, Select } from '../components/ui';
import { cn, formatTimestamp } from '../lib/utils';
import {
    SERVICE_REQUEST_PRIORITY_LABELS,
    SERVICE_REQUEST_STATUS_LABELS,
} from '../lib/database.types';
import type {
    Crew,
    ServiceRequestPriority,
    ServiceRequestStatus,
    ServiceRequestWithContext,
} from '../lib/database.types';

type View = 'open' | 'closed';

const OPEN_STATUSES: ServiceRequestStatus[] = ['new', 'assigned', 'in_progress'];
const CLOSED_STATUSES: ServiceRequestStatus[] = ['complete', 'cancelled'];

// -----------------------------------------------------------------------------
// Pills
// -----------------------------------------------------------------------------

const PRIORITY_STYLES: Record<ServiceRequestPriority, string> = {
    emergency: 'bg-amber-500 text-graphite-950',
    urgent: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/40',
    normal: 'bg-graphite-200 dark:bg-graphite-800 text-graphite-600 dark:text-graphite-400',
    low: 'bg-graphite-100 dark:bg-graphite-900 text-graphite-500',
};

const STATUS_STYLES: Record<ServiceRequestStatus, string> = {
    new: 'bg-signal-500/10 text-signal-700 dark:text-signal-400 border border-signal-500/30',
    assigned: 'bg-graphite-200 dark:bg-graphite-800 text-graphite-700 dark:text-graphite-300',
    in_progress: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30',
    complete: 'bg-done-500/10 text-done-500 border border-done-500/30',
    cancelled: 'bg-graphite-200/60 dark:bg-graphite-800/60 text-graphite-500',
};

function Pill({ className, label }: { className: string; label: string }) {
    return (
        <span
            className={cn(
                'inline-block px-1.5 py-0.5 rounded font-mono text-[10px] uppercase tracking-wider',
                className
            )}
        >
            {label}
        </span>
    );
}

// -----------------------------------------------------------------------------
// Service request card
// -----------------------------------------------------------------------------

function ServiceRequestCard({
    request,
    crews,
}: {
    request: ServiceRequestWithContext;
    crews: Crew[];
}) {
    const assignMut = useAssignServiceRequest();
    const advanceMut = useAdvanceServiceRequestStatus();
    const cancelMut = useCancelServiceRequest();

    const [selectedCrewId, setSelectedCrewId] = useState(request.assigned_crew_id ?? '');
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [error, setError] = useState<string | null>(null);

    const busy = assignMut.isPending || advanceMut.isPending || cancelMut.isPending;
    const isOpen =
        request.status === 'new' ||
        request.status === 'assigned' ||
        request.status === 'in_progress';

    async function run(fn: () => Promise<unknown>) {
        setError(null);
        try {
            await fn();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Action failed');
        }
    }

    function handleAssign() {
        if (!selectedCrewId) return;
        void run(() =>
            assignMut.mutateAsync({ p_request_id: request.id, p_crew_id: selectedCrewId })
        );
    }

    function handleAdvance(next: 'in_progress' | 'complete') {
        void run(() =>
            advanceMut.mutateAsync({ p_request_id: request.id, p_new_status: next })
        );
    }

    function handleCancel() {
        if (!cancelReason.trim()) return;
        void run(async () => {
            await cancelMut.mutateAsync({
                p_request_id: request.id,
                p_reason: cancelReason.trim(),
            });
            setCancelOpen(false);
            setCancelReason('');
        });
    }

    return (
        <div
            className={cn(
                'border rounded px-4 py-3.5',
                request.priority === 'emergency'
                    ? 'border-amber-500/40 bg-amber-500/[0.04]'
                    : 'border-graphite-200 dark:border-graphite-800 bg-white dark:bg-graphite-900'
            )}
        >
            {/* Header */}
            <div className="flex items-center flex-wrap gap-2 mb-1.5">
                <span className="font-mono text-xs text-graphite-500 tabular">
                    {request.request_number ?? '—'}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-graphite-500">
                    {request.request_type.label}
                </span>
                <Pill
                    className={PRIORITY_STYLES[request.priority]}
                    label={SERVICE_REQUEST_PRIORITY_LABELS[request.priority]}
                />
                <Pill
                    className={STATUS_STYLES[request.status]}
                    label={SERVICE_REQUEST_STATUS_LABELS[request.status]}
                />
            </div>

            {/* Subject + description */}
            <div className="font-display font-semibold text-graphite-900 dark:text-graphite-100">
                {request.subject}
            </div>
            {request.description ? (
                <p className="text-sm text-graphite-600 dark:text-graphite-400 mt-0.5 whitespace-pre-wrap">
                    {request.description}
                </p>
            ) : null}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-graphite-500">
                {request.job ? (
                    <span className="font-mono">
                        {request.job.community.name} · Lot {request.job.lot_number}
                    </span>
                ) : (
                    <span className="font-mono text-graphite-400 dark:text-graphite-600">
                        No job linked
                    </span>
                )}
                {request.assigned_crew ? (
                    <span className="font-mono uppercase tracking-wider text-signal-700 dark:text-signal-400">
                        Crew {request.assigned_crew.name}
                    </span>
                ) : null}
                <span className="font-mono">
                    {request.created_by_name ?? request.created_by_email ?? 'Unknown'}
                </span>
                <span className="font-mono text-graphite-400 dark:text-graphite-600">
                    {formatTimestamp(request.created_at)}
                </span>
            </div>

            {/* Terminal detail */}
            {request.status === 'complete' ? (
                <div className="mt-2 text-xs font-mono text-done-500">
                    Completed {request.completed_at ? formatTimestamp(request.completed_at) : ''}
                    {request.completed_by_email ? ` by ${request.completed_by_email}` : ''}
                    {request.completion_notes ? (
                        <span className="block text-graphite-500 mt-0.5">
                            {request.completion_notes}
                        </span>
                    ) : null}
                </div>
            ) : null}
            {request.status === 'cancelled' ? (
                <div className="mt-2 text-xs font-mono text-graphite-500">
                    Cancelled {request.cancelled_at ? formatTimestamp(request.cancelled_at) : ''}
                    {request.cancelled_by_email ? ` by ${request.cancelled_by_email}` : ''}
                    {request.cancellation_reason ? (
                        <span className="block mt-0.5">Reason: {request.cancellation_reason}</span>
                    ) : null}
                </div>
            ) : null}

            {/* Actions */}
            {isOpen ? (
                <div className="mt-3 pt-3 border-t border-graphite-200 dark:border-graphite-800">
                    {error ? (
                        <div className="mb-2.5 border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-3 py-2 rounded font-mono text-xs">
                            {error}
                        </div>
                    ) : null}

                    {cancelOpen ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <div className="flex-1 min-w-[200px]">
                                <Input
                                    id={`cancel-reason-${request.id}`}
                                    placeholder="Reason for cancellation"
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                />
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleCancel}
                                    disabled={busy || !cancelReason.trim()}
                                >
                                    Confirm cancellation
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        setCancelOpen(false);
                                        setCancelReason('');
                                        setError(null);
                                    }}
                                    disabled={busy}
                                >
                                    Keep request
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="w-40">
                                <Select
                                    id={`crew-${request.id}`}
                                    value={selectedCrewId}
                                    onChange={(e) => setSelectedCrewId(e.target.value)}
                                >
                                    <option value="">Select crew</option>
                                    {crews.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Select>
                            </div>

                            {request.status === 'new' ? (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAssign}
                                    disabled={busy || !selectedCrewId}
                                >
                                    Assign
                                </Button>
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleAssign}
                                    disabled={
                                        busy ||
                                        !selectedCrewId ||
                                        selectedCrewId === request.assigned_crew_id
                                    }
                                >
                                    Reassign
                                </Button>
                            )}

                            {request.status === 'assigned' ? (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleAdvance('in_progress')}
                                    disabled={busy}
                                >
                                    Start
                                </Button>
                            ) : null}

                            {request.status === 'assigned' || request.status === 'in_progress' ? (
                                <Button
                                    variant={request.status === 'in_progress' ? 'primary' : 'secondary'}
                                    size="sm"
                                    onClick={() => handleAdvance('complete')}
                                    disabled={busy}
                                >
                                    Complete
                                </Button>
                            ) : null}

                            <div className="flex-1" />

                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    setCancelOpen(true);
                                    setError(null);
                                }}
                                disabled={busy}
                            >
                                Cancel request
                            </Button>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
}

// -----------------------------------------------------------------------------
// Dispatch board page
// -----------------------------------------------------------------------------

export function DispatchBoardPage() {
    const [view, setView] = useState<View>('open');
    const { data: requests, isLoading, error } = useServiceRequests({
        status: view === 'open' ? OPEN_STATUSES : CLOSED_STATUSES,
    });
    const { data: crews = [] } = useCrews();

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-1">
                    <h1 className="font-display font-bold text-2xl text-graphite-900 dark:text-graphite-50">
                        Dispatch
                    </h1>
                    <span className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                        {requests?.length ?? 0} {view}
                    </span>
                </div>
                <p className="text-sm text-graphite-600 dark:text-graphite-400">
                    Service requests from the field. Assign a crew, move work forward, close out
                    what is done.
                </p>
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-1 mb-4">
                {(['open', 'closed'] as View[]).map((v) => (
                    <button
                        key={v}
                        type="button"
                        onClick={() => setView(v)}
                        className={cn(
                            'font-mono text-xs uppercase tracking-widest px-3 py-1.5 rounded transition-colors',
                            view === v
                                ? 'text-graphite-900 bg-graphite-200 dark:text-graphite-50 dark:bg-graphite-800'
                                : 'text-graphite-600 hover:text-graphite-900 dark:text-graphite-500 dark:hover:text-graphite-200'
                        )}
                    >
                        {v}
                    </button>
                ))}
            </div>

            {error ? (
                <div className="mb-4 border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                    Error loading service requests: {(error as Error).message}
                </div>
            ) : null}

            {isLoading ? (
                <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                    Loading...
                </div>
            ) : !requests || requests.length === 0 ? (
                <EmptyState
                    title={view === 'open' ? 'No open requests' : 'No closed requests'}
                    description={
                        view === 'open'
                            ? 'Nothing in the dispatch queue right now.'
                            : 'No completed or cancelled requests yet.'
                    }
                />
            ) : (
                <div className="space-y-2.5">
                    {requests.map((request) => (
                        <ServiceRequestCard key={request.id} request={request} crews={crews} />
                    ))}
                </div>
            )}
        </div>
    );
}
